import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import {
  Presentation as PresentationIcon,
  X,
  Edit3,
  Check,
  Loader2,
  Play,
  ChevronLeft,
  ChevronRight,
  Layout,
} from 'lucide-react';
import clsx from 'clsx';
import type { Presentation as PresentationType } from '@/types';
import { SlidesSidebar, type Slide } from '@/components/presentation/SlidesSidebar';
import { SlideEditor } from '@/components/presentation/SlideEditor';
import { FullscreenMode } from '@/components/presentation/FullscreenMode';
import { updatePresentation } from '@/db/projects';
import { useAppStore } from '@/store/appStore';
import { handleError } from '@/utils/errorHandler';

interface PresentationViewProps {
  presentation: PresentationType;
  onUpdate?: (id: string, updates: Partial<PresentationType>) => Promise<void>;
  onClose?: () => void;
}

function generateSlideId(): string {
  return `slide-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function createNewSlide(template: Slide['template']): Slide {
  return {
    id: generateSlideId(),
    title: '',
    content: '',
    template,
  };
}

export function PresentationView({ presentation, onUpdate, onClose }: PresentationViewProps) {
  const updateCurrentItem = useAppStore((state) => state.updateCurrentItem);
  // Parse initial slides from content
  const initialSlides = useMemo<Slide[]>(() => {
    const content = presentation.content as unknown[];
    if (Array.isArray(content) && content.length > 0) {
      return content.map((item: unknown, idx) => {
        const slide = item as Partial<Slide>;
        return {
          id: slide.id || `slide-${idx}`,
          title: slide.title || '',
          content: slide.content || '',
          template: slide.template || 'content',
        };
      });
    }
    // Default first slide
    return [createNewSlide('title')];
  }, [presentation.content]);

  const [slides, setSlides] = useState<Slide[]>(initialSlides);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [title, setTitle] = useState(presentation.name);

  const titleInputRef = useRef<HTMLInputElement>(null);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hasUnsavedChangesRef = useRef(false);

  // Warn before leaving with unsaved changes
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChangesRef.current) {
        e.preventDefault();
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, []);

  const currentSlide = slides[currentSlideIndex];

  // Sync slides with prop changes (e.g., when presentation is updated externally)
  useEffect(() => {
    const content = presentation.content as unknown[];
    if (Array.isArray(content) && content.length > 0) {
      const newSlides = content.map((item: unknown, idx) => {
        const slide = item as Partial<Slide>;
        return {
          id: slide.id || `slide-${idx}`,
          title: slide.title || '',
          content: slide.content || '',
          template: slide.template || 'content',
        };
      });
      setSlides((prevSlides) => {
        // Only update if slides actually changed to avoid unnecessary re-renders
        if (JSON.stringify(newSlides) !== JSON.stringify(prevSlides)) {
          return newSlides;
        }
        return prevSlides;
      });
    }
  }, [presentation.content]);

  // Sync title with prop changes
  useEffect(() => {
    setTitle(presentation.name);
  }, [presentation.name]);

  // Auto-save with debounce
  const saveData = useCallback(async () => {
    setIsSaving(true);
    try {
      const updated = await updatePresentation(presentation.id, { content: slides });
      if (updated) {
        // Update store if item is current
        updateCurrentItem({ content: slides });
        if (onUpdate) {
          await onUpdate(presentation.id, { content: slides });
        }
      }
      hasUnsavedChangesRef.current = false;
      setLastSaved(new Date());
    } catch (error) {
      handleError(error, { level: 'error', message: 'Error saving presentation' });
    } finally {
      setIsSaving(false);
    }
  }, [slides, presentation.id, onUpdate, updateCurrentItem]);

  // Debounced save
  useEffect(() => {
    hasUnsavedChangesRef.current = true;
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    saveTimeoutRef.current = setTimeout(saveData, 2000);
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [slides, saveData]);

  // Focus title input when editing
  useEffect(() => {
    if (isEditingTitle && titleInputRef.current) {
      titleInputRef.current.focus();
      titleInputRef.current.select();
    }
  }, [isEditingTitle]);

  // Slide management
  const handleSelectSlide = useCallback((index: number) => {
    setCurrentSlideIndex(index);
  }, []);

  const handleAddSlide = useCallback(
    (template: Slide['template']) => {
      const newSlide = createNewSlide(template);
      setSlides((prev) => {
        const newSlides = [...prev];
        newSlides.splice(currentSlideIndex + 1, 0, newSlide);
        return newSlides;
      });
      setCurrentSlideIndex(currentSlideIndex + 1);
    },
    [currentSlideIndex]
  );

  const handleDuplicateSlide = useCallback(
    (index: number) => {
      const slideToDuplicate = slides[index];
      const duplicatedSlide: Slide = {
        ...slideToDuplicate,
        id: generateSlideId(),
      };
      setSlides((prev) => {
        const newSlides = [...prev];
        newSlides.splice(index + 1, 0, duplicatedSlide);
        return newSlides;
      });
      setCurrentSlideIndex(index + 1);
    },
    [slides]
  );

  const handleDeleteSlide = useCallback(
    (index: number) => {
      if (slides.length <= 1) return;

      setSlides((prev) => prev.filter((_, i) => i !== index));
      if (currentSlideIndex >= index && currentSlideIndex > 0) {
        setCurrentSlideIndex(currentSlideIndex - 1);
      }
    },
    [slides.length, currentSlideIndex]
  );

  const handleReorderSlides = useCallback(
    (fromIndex: number, toIndex: number) => {
      setSlides((prev) => {
        const newSlides = [...prev];
        const [movedSlide] = newSlides.splice(fromIndex, 1);
        newSlides.splice(toIndex, 0, movedSlide);
        return newSlides;
      });

      // Update current index if needed
      if (currentSlideIndex === fromIndex) {
        setCurrentSlideIndex(toIndex);
      } else if (fromIndex < currentSlideIndex && toIndex >= currentSlideIndex) {
        setCurrentSlideIndex(currentSlideIndex - 1);
      } else if (fromIndex > currentSlideIndex && toIndex <= currentSlideIndex) {
        setCurrentSlideIndex(currentSlideIndex + 1);
      }
    },
    [currentSlideIndex]
  );

  const handleUpdateSlide = useCallback(
    (updates: Partial<Slide>) => {
      setSlides((prev) =>
        prev.map((slide, i) => (i === currentSlideIndex ? { ...slide, ...updates } : slide))
      );
    },
    [currentSlideIndex]
  );

  // Navigation
  const handlePrevSlide = useCallback(() => {
    if (currentSlideIndex > 0) {
      setCurrentSlideIndex(currentSlideIndex - 1);
    }
  }, [currentSlideIndex]);

  const handleNextSlide = useCallback(() => {
    if (currentSlideIndex < slides.length - 1) {
      setCurrentSlideIndex(currentSlideIndex + 1);
    }
  }, [currentSlideIndex, slides.length]);

  // Title editing
  const handleTitleSave = useCallback(async () => {
    if (!title.trim()) {
      setTitle(presentation.name);
      setIsEditingTitle(false);
      return;
    }

    if (title !== presentation.name) {
      setIsSaving(true);
      try {
        const updated = await updatePresentation(presentation.id, { name: title });
        if (updated) {
          // Update local state
          setTitle(title);
          // Update store if item is current
          updateCurrentItem({ name: title });
          if (onUpdate) {
            await onUpdate(presentation.id, { name: title });
          }
        }
        setLastSaved(new Date());
      } catch (error) {
        handleError(error, { level: 'error', message: 'Errore nel salvataggio del titolo' });
        setTitle(presentation.name);
      } finally {
        setIsSaving(false);
      }
    }
    setIsEditingTitle(false);
  }, [presentation.id, presentation.name, title, onUpdate, updateCurrentItem]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't handle if in an input
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return;
      }

      if (e.key === 'F5' || (e.key === 'Enter' && e.metaKey)) {
        e.preventDefault();
        setIsFullscreen(true);
      } else if (e.key === 'ArrowLeft' && !isFullscreen) {
        e.preventDefault();
        handlePrevSlide();
      } else if (e.key === 'ArrowRight' && !isFullscreen) {
        e.preventDefault();
        handleNextSlide();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isFullscreen, handlePrevSlide, handleNextSlide]);

  if (isFullscreen) {
    return (
      <FullscreenMode
        slides={slides}
        currentIndex={currentSlideIndex}
        onNavigate={setCurrentSlideIndex}
        onExit={() => setIsFullscreen(false)}
      />
    );
  }

  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-surface-primary">
      {/* Header */}
      <div className="flex items-center justify-between px-4 sm:px-6 py-3 border-b border-border-primary bg-surface-secondary">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="p-1.5 rounded-lg bg-accent-purple/10">
            <Layout className="w-4 h-4 text-accent-purple" />
          </div>

          {isEditingTitle ? (
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <input
                ref={titleInputRef}
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleTitleSave();
                  else if (e.key === 'Escape') {
                    setTitle(presentation.name);
                    setIsEditingTitle(false);
                  }
                }}
                onBlur={handleTitleSave}
                className={clsx(
                  'flex-1 min-w-0 px-2 py-1 text-base font-semibold rounded',
                  'bg-surface-primary border border-border-primary text-text-primary',
                  'focus:outline-none focus:ring-2 focus:ring-accent-violet focus:border-transparent'
                )}
              />
              <button
                onClick={handleTitleSave}
                className="p-1 rounded hover:bg-surface-hover text-accent-emerald"
              >
                <Check className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <button
              onClick={() => setIsEditingTitle(true)}
              className="group flex items-center gap-2 min-w-0"
            >
              <h1 className="text-base font-semibold text-text-primary truncate">
                {presentation.name}
              </h1>
              <Edit3 className="w-3 h-3 text-text-quaternary opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
            </button>
          )}

          {/* Slide indicator badge */}
          <div className="hidden sm:flex items-center gap-1.5 px-2 py-1 rounded-md bg-surface-tertiary text-xs text-text-tertiary">
            <PresentationIcon className="w-3 h-3" />
            <span>
              {currentSlideIndex + 1}/{slides.length}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Save indicator */}
          <div className="hidden sm:flex items-center gap-2 text-xs text-text-quaternary mr-2">
            {isSaving ? (
              <>
                <Loader2 className="w-3 h-3 animate-spin" />
                <span>Salvataggio...</span>
              </>
            ) : lastSaved ? (
              <span>
                Salvato alle{' '}
                {lastSaved.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
              </span>
            ) : null}
          </div>

          {/* Present button */}
          <button
            onClick={() => setIsFullscreen(true)}
            className={clsx(
              'flex items-center gap-2 px-3 py-1.5 rounded-lg',
              'bg-accent-violet text-white hover:bg-accent-violet/90 transition-colors',
              'text-sm font-medium shadow-sm'
            )}
          >
            <Play className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Presenta</span>
          </button>

          {onClose && (
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg hover:bg-surface-hover transition-colors text-text-quaternary hover:text-text-secondary"
              title="Chiudi presentazione"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Slides sidebar */}
        <SlidesSidebar
          slides={slides}
          currentSlideIndex={currentSlideIndex}
          onSelectSlide={handleSelectSlide}
          onAddSlide={handleAddSlide}
          onDuplicateSlide={handleDuplicateSlide}
          onDeleteSlide={handleDeleteSlide}
          onReorderSlides={handleReorderSlides}
        />

        {/* Slide editor */}
        <div className="flex-1 flex flex-col overflow-hidden bg-surface-tertiary/30">
          <div className="flex-1 overflow-auto">
            {currentSlide && <SlideEditor slide={currentSlide} onUpdate={handleUpdateSlide} />}
          </div>

          {/* Navigation bar */}
          <div className="flex items-center justify-between px-4 py-2 border-t border-border-primary bg-surface-secondary/80 backdrop-blur-sm">
            <button
              onClick={handlePrevSlide}
              disabled={currentSlideIndex === 0}
              className={clsx(
                'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm transition-colors',
                currentSlideIndex === 0
                  ? 'text-text-quaternary cursor-not-allowed'
                  : 'text-text-secondary hover:bg-surface-hover hover:text-text-primary'
              )}
            >
              <ChevronLeft className="w-4 h-4" />
              <span className="hidden sm:inline">Precedente</span>
            </button>

            {/* Slide dots */}
            <div className="flex items-center gap-1.5">
              {slides.length <= 10 ? (
                slides.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentSlideIndex(idx)}
                    className={clsx(
                      'rounded-full transition-all',
                      idx === currentSlideIndex
                        ? 'w-6 h-1.5 bg-accent-violet'
                        : 'w-1.5 h-1.5 bg-border-primary hover:bg-text-quaternary'
                    )}
                  />
                ))
              ) : (
                <div className="flex items-center gap-2 px-3 py-1 rounded-lg bg-surface-tertiary">
                  <span className="text-sm font-medium text-text-primary">
                    {currentSlideIndex + 1}
                  </span>
                  <span className="text-xs text-text-quaternary">di {slides.length}</span>
                </div>
              )}
            </div>

            <button
              onClick={handleNextSlide}
              disabled={currentSlideIndex === slides.length - 1}
              className={clsx(
                'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm transition-colors',
                currentSlideIndex === slides.length - 1
                  ? 'text-text-quaternary cursor-not-allowed'
                  : 'text-text-secondary hover:bg-surface-hover hover:text-text-primary'
              )}
            >
              <span className="hidden sm:inline">Successiva</span>
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
